/**
 * editable-capabilities.js — what row 2 may offer for a given editable type.
 *
 * Panes ASK; they never branch on `editableType` themselves. That branching is
 * exactly how the first plan draft came to assume "Replace" was a universal
 * fallback action — it is not. Buttons, containers, embeds and form fields do
 * not share it, and a pane that guessed would render a control with no writer
 * behind it.
 *
 * ── The registry is deliberately incomplete ───────────────────────────────
 * A type appears here with `controls` ONLY where a real writer has been
 * verified to exist and to be reachable from the parent. Everything else
 * declares `controls: []` and `allSettings: 'glass'`, which renders as
 * "label + All settings" and nothing more.
 *
 * That is the honest state of the codebase today, not a placeholder: crop, fit,
 * link and alt for images live inside `glass-panel-image.js`; the button,
 * container, embed, media and field panels have not been opened up at all.
 * Declaring a control here that no service can perform would move the failure
 * from "the pane is sparse" to "the button does nothing", which is worse.
 *
 * ── Disabled state is COPY, not a boolean ─────────────────────────────────
 * `disabledReason` returns the sentence a user reads. It goes into
 * `aria-label`, because a disabled control does not reliably receive hover or
 * focus — the same reason `_syncUndoRedoChrome` in today's toolbar swaps its
 * label instead of relying on a tooltip.
 */

/**
 * Row-2 control ids. Shared vocabulary so two types that offer the same control
 * render it identically and in the same relative order.
 */
export const CONTROL = Object.freeze({
  FONT: 'font',
  SIZE: 'size',
  BOLD: 'bold',
  ITALIC: 'italic',
  ALIGN: 'align',
  COLOR: 'color',
  LINK: 'link',
  REWRITE: 'rewrite',
  REPLACE: 'replace',
  CROP: 'crop',
  FIT: 'fit',
  ALT: 'alt'
});

/**
 * `controls` is ORDERED — the pane renders in registry order.
 * `allSettings`: 'glass' opens the floating panel, 'drawer' the section-
 * properties drawer, null means the type has no deeper surface and the pane
 * shows no "All settings" affordance at all.
 */
const REGISTRY = Object.freeze({
  text: {
    controls: [
      CONTROL.FONT,
      CONTROL.SIZE,
      CONTROL.BOLD,
      CONTROL.ITALIC,
      CONTROL.ALIGN,
      CONTROL.COLOR,
      CONTROL.LINK,
      CONTROL.REWRITE
    ],
    allSettings: 'glass'
  },
  image: {
    // crop/fit remain inside glass-panel-image.js — see the header. Only the
    // three with a verified parent-reachable writer are declared.
    controls: [CONTROL.REPLACE, CONTROL.LINK, CONTROL.ALT],
    allSettings: 'glass'
  },
  // Registered glass-panel types with no lifted writer yet. Present on purpose:
  // an ABSENT type and a type with no controls are different states, and only
  // the second one is a deliberate decision.
  button: { controls: [], allSettings: 'glass' },
  // LG-3. Replace and Size are the two things anyone does to a logo, and both
  // now have a parent-reachable writer in `logo-command-service.js`. Size is a
  // HEIGHT: a header is a horizontal band, so height is the constrained
  // dimension and width follows from the asset.
  logo: { controls: [CONTROL.REPLACE, CONTROL.SIZE, CONTROL.ALT], allSettings: 'glass' },
  svg: { controls: [], allSettings: 'glass' },
  container: { controls: [], allSettings: 'glass' },
  embed: { controls: [], allSettings: 'glass' },
  media: { controls: [], allSettings: 'glass' },
  field: { controls: [], allSettings: 'glass' }
});

const KIND_REGISTRY = Object.freeze({
  photo: { controls: [CONTROL.REPLACE, CONTROL.LINK, CONTROL.ALT], allSettings: 'glass' },
  logo: { controls: [CONTROL.REPLACE, CONTROL.SIZE, CONTROL.ALT], allSettings: 'glass' },
  icon: { controls: [CONTROL.REPLACE, CONTROL.COLOR], allSettings: 'glass' },
  decoration: { controls: [CONTROL.REPLACE, CONTROL.COLOR], allSettings: 'glass' },
  background: { controls: [CONTROL.REPLACE], allSettings: 'glass' }
});

/** Which controls need a live text selection inside the iframe to mean anything. */
const NEEDS_TEXT_EDIT = new Set([
  CONTROL.BOLD,
  CONTROL.ITALIC,
  CONTROL.COLOR,
  CONTROL.FONT,
  CONTROL.LINK,
  CONTROL.REWRITE
]);

/**
 * @typedef {Object} CapabilityContext
 * @property {boolean} [writable]      — `store.isWritable()`
 * @property {boolean} [locked]        — a peer holds the section lock
 * @property {boolean} [editingText]   — the iframe has an active text editable
 * @property {boolean} [hasAlt]        — image only
 */

/**
 * @param {string} editableType
 * @returns {{controls: string[], allSettings: 'glass'|'drawer'|null,
 *            disabledReason: (controlId: string, ctx?: CapabilityContext) => string|null}}
 */
export function capabilitiesFor(editableType) {
  const entry = REGISTRY[editableType] || null;
  const controls = entry ? entry.controls.slice() : [];
  // An unregistered type gets NO controls and NO assumed settings surface.
  // There is no universal fallback action, which is the whole point.
  const allSettings = entry ? entry.allSettings : null;

  return {
    controls,
    allSettings,
    disabledReason(controlId, ctx = {}) {
      if (!controls.includes(controlId)) return 'Not available for this element';
      // Precedence matches section-command-service: read-only outranks a peer
      // lock, because in a read-only tab nothing is editable and naming a peer
      // would imply that resolving the lock would help.
      if (ctx.writable === false) return 'This tab is read-only — edit in the other tab';
      if (ctx.locked) return 'Someone else is editing this section';
      if (NEEDS_TEXT_EDIT.has(controlId) && editableType === 'text' && !ctx.editingText) {
        return 'Click into the text first';
      }
      return null;
    }
  };
}

/**
 * Two-axis capability resolver. CSS provenance may make a visible target
 * selectable but read-only; callers receive the refusal reason with no dead
 * controls rather than guessing from editableType.
 */
export function capabilitiesForTarget(target = {}) {
  const kindEntry = KIND_REGISTRY[target.kind] || null;
  const base = kindEntry || REGISTRY[target.editableType] || null;
  const refusalReason =
    target.provenance?.writable === false
      ? target.provenance.reason || 'This background source cannot be edited here'
      : null;
  let controls = refusalReason ? [] : (base?.controls || []).slice();
  if (
    (target.kind === 'icon' || target.kind === 'decoration') &&
    (target.format === 'raster' || target.editableType === 'image')
  ) {
    controls = controls.filter((control) => control !== CONTROL.COLOR);
  }
  const allSettings = base?.allSettings ?? null;
  return {
    controls,
    sharedControls: controls.includes(CONTROL.REPLACE) ? [CONTROL.REPLACE] : [],
    jumpTargets:
      target.kind === 'photo' || target.editableType === 'image'
        ? { [CONTROL.ALT]: '#pb-gp-img-alt' }
        : {},
    allSettings,
    refusalReason,
    disabledReason(controlId, ctx = {}) {
      if (refusalReason) return refusalReason;
      if (!controls.includes(controlId)) return 'Not available for this element';
      if (ctx.writable === false) return 'This tab is read-only — edit in the other tab';
      if (ctx.locked) return 'Someone else is editing this section';
      if (NEEDS_TEXT_EDIT.has(controlId) && target.editableType === 'text' && !ctx.editingText) {
        return 'Click into the text first';
      }
      return null;
    }
  };
}

export function panelKeyFor({ editableType, kind, format, provenance } = {}) {
  if (provenance?.panelKey) return provenance.panelKey;
  if (kind === 'background' && editableType === 'css-background') return 'background';
  if (kind === 'decoration') return 'decoration';
  if (kind === 'icon' && format === 'raster') return 'image';
  return editableType || null;
}

/** True when the type has a registry entry at all (registered vs unknown). */
export function isKnownEditableType(editableType) {
  return Object.prototype.hasOwnProperty.call(REGISTRY, editableType);
}

/** Every type the registry knows, for tests and for the census. */
export function knownEditableTypes() {
  return Object.keys(REGISTRY).sort();
}
